import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField, 
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  IconButton
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material"; // <- Correctly import icons
import moment from "moment";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import { FETCH_CLIENT_JOBS } from "../../constant/Apipath";
import { ClientEnglishFallback } from "../constant/ClientEnglishFallback";
import { BILightGrey } from "../../assets/helpers";

const GeneralComponent = ({
  setGeneralComponentData,
  typeId,
  userId,
  inspectionRecord
}) => {
  const { selectedClientData, loginData, multiLanguageData } = useSelector((state) => state?.main);
  const [showJobModal, setShowJobModal] = useState(false);
  const [searchInputText, setSearchInputText] = useState("");
  const [jobLists, setJobLists] = useState([]);
  const [jobName, setJobName] = useState("");
  const [equipmentCode, setEquipmentCode] = useState("");
  const [assetCode, setAssetCode] = useState("");
  const [comment, setComment] = useState("");
  const [jobDisabled, setJobDisabled] = useState(false);
  const [equipmentDisabled, setEquipmentDisabled] = useState(false);
  const LANGUAGE = ClientEnglishFallback.GeneralComponentStatic(multiLanguageData?.GeneralComponentStatic || {});
  const { TEXT_GENERAL_COMPONENT,
    TEXT_JOB,
    TEXT_EQUIPMENT,
    TEXT_ASSET_CODE,
    TEXT_SELECT_JOB,
    TEXT_ENTER_EQUIPMENT_NUMBER,
    TEXT_ENTER_ASSET_CODE,
    TEXT_COMMENTS,
    TEXT_COMMENTS_INFO,
    TEXT_SEARCH_JOBS,
    TEXT_NO_JOB,
    TEXT_SELECT_A_JOB,
  } = LANGUAGE;

  useEffect(() => {
    const nextDate = inspectionRecord?.date
      ? moment(inspectionRecord.date).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    const hasAsset = Boolean(inspectionRecord?.fixedAssetId);
    const nextEquipmentValue = hasAsset
      ? inspectionRecord?.assetCode || ""
      : inspectionRecord?.equipmentCode || "";

    setJobName(
      inspectionRecord?.clientJobId
        ? `${inspectionRecord?.clientJobCode || ""}${inspectionRecord?.clientJobCode && inspectionRecord?.clientJobName ? " - " : ""}${inspectionRecord?.clientJobName || ""}`
        : ""
    );
    setEquipmentCode(hasAsset ? "" : nextEquipmentValue);
    setAssetCode(hasAsset ? nextEquipmentValue : "");
    setComment(inspectionRecord?.comments || "");
    setJobDisabled(Boolean(inspectionRecord?.clientJobId));
    setEquipmentDisabled(Boolean(nextEquipmentValue));
    setGeneralComponentData({
      UserId: userId,
      ReturnRecordId: true,
      ReturnRecordError: true,
      SaveList: [
        {
          Id: inspectionRecord?.id || 0,
          ClientId: Number(selectedClientData?.ClientId),
          ClientEmployeeId: Number(selectedClientData?.ClientEmployeeId),
          InspectionTypeId: typeId,
          Date: nextDate,
          IsSubmitted: true,
          ModifiedBy: userId,
          ClientJobId: inspectionRecord?.clientJobId || 0,
          FixedAssetId: inspectionRecord?.fixedAssetId || 0,
          EquipmentCode: nextEquipmentValue,
          AssetCode: hasAsset ? nextEquipmentValue : "",
          Comments: inspectionRecord?.comments || ""
        }
      ]
    });
  }, [inspectionRecord, selectedClientData?.ClientId, selectedClientData?.ClientEmployeeId, typeId, userId, setGeneralComponentData]);

  const fetchClientJobs = useCallback(
    async (search) => {
      try {
        const clientJobObj = {
          PageNr: 1,
          FullSearch: search,
          UserId: Number(userId),
          IncludeRecordNr: true,
          FetchAllowedRecordsOnly: true,
          SearchList: [{ ClientId: Number(selectedClientData?.ClientId), active: true }],
          SortList: [{ FieldName: "dateCreated", Direction: "DESC" }]
        };

        const res = await Postrequestcall(FETCH_CLIENT_JOBS, clientJobObj, loginData?.accessToken);
        const options =
          res?.data?.data?.map((item) => ({
            id: item.id,
            value: `${item.jobCode} - ${item.name}`,
            label: `${item.jobCode} - ${item.name}`
          })) || [];
        setJobLists(options);
      } catch (err) {
        console.error("fetchClientJobs-> Error fetching jobs:", err);
      }
    },
    [selectedClientData, userId, loginData?.accessToken]
  );

  useEffect(() => {
    fetchClientJobs(searchInputText);
  }, [searchInputText, fetchClientJobs]);

  const handleJobSelect = (job) => {
    setJobName(job.value);
    setShowJobModal(false);

    setGeneralComponentData((prev) => ({
      ...prev,
      SaveList: [
        {
          ...(prev.SaveList?.[0] || {}),
          ClientJobId: job.id
        }
      ]
    }));
  };

  const handleEquipmentChange = (text) => {
    setEquipmentCode(text);
    setGeneralComponentData((prev) => ({
      ...prev,
      SaveList: [
        {
          ...(prev.SaveList?.[0] || {}),
          EquipmentCode: text
        }
      ]
    }));
  };

  const handleAssetChange = (text) => {
    setAssetCode(text);
    setGeneralComponentData((prev) => ({
      ...prev,
      SaveList: [
        {
          ...(prev.SaveList?.[0] || {}),
          EquipmentCode: text,
          FixedAssetId: inspectionRecord?.fixedAssetId || 0,
          AssetCode: text
        }
      ]
    }));
  };

  const handleCommentChange = (text) => {
    setComment(text);
    setGeneralComponentData((prev) => ({
      ...prev,
      SaveList: [
        {
          ...(prev.SaveList?.[0] || {}),
          Comments: text
        }
      ]
    }));
  };

  const handleClearSearch = () => {
    setSearchInputText("");
  };

  const handleSearchIconClick = () => {
    fetchClientJobs(searchInputText);
  };

  const handleModalClose = () => {
    setShowJobModal(false);
    setSearchInputText("");
    setJobLists([]); 
  };

  return (
    <Box sx={{ padding:'6px 0px 18px 0px' }}>
      <Typography variant="h5" sx={{ mb: 2,  fontWeight: "bold" }} >
        {TEXT_GENERAL_COMPONENT}
      </Typography>

      <Box sx={{ backgroundColor: "#fff", borderRadius: 2, p: 3, boxShadow: 1 }}> 
        <Typography sx={{ color: "#4f4f4f", mb: 1 }}>{TEXT_JOB}</Typography>
        <TextField
          fullWidth
          variant="outlined"
          value={jobName}
          placeholder={TEXT_SELECT_JOB}
          onClick={() => {
            if (!jobDisabled) {
              setShowJobModal(true);
            }
          }}
          InputProps={{ readOnly: true }}
          disabled={jobDisabled}
          sx={{ mb: 2 }}
        />
 
        <Typography sx={{ color: "#4f4f4f", mb: 1 }}>
          {inspectionRecord?.fixedAssetId ? TEXT_ASSET_CODE : TEXT_EQUIPMENT}
        </Typography>
        <TextField
          fullWidth
          placeholder={inspectionRecord?.fixedAssetId ? TEXT_ENTER_ASSET_CODE : TEXT_ENTER_EQUIPMENT_NUMBER}
          variant="outlined"
          value={inspectionRecord?.fixedAssetId ? assetCode : equipmentCode}
          onChange={(e) => {
            let inputValue = e.target.value;
            inputValue = inputValue.replace(/^[^A-Za-z0-9]+/, "");
            if (inspectionRecord?.fixedAssetId) {
              handleAssetChange(inputValue);
            } else {
              handleEquipmentChange(inputValue);
            }
          }}
          disabled={equipmentDisabled}
          sx={{ mb: 2 }}
        />
 
        <Typography sx={{ color: "#4f4f4f", mb: 1 }}>{TEXT_COMMENTS}</Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={6}  
          placeholder={TEXT_COMMENTS_INFO}
          variant="outlined"
          value={comment}
          onChange={(e) => {
            let inputValue = e.target.value;
            inputValue = inputValue.replace(/^[^A-Za-z0-9]+/, "");
            handleCommentChange(inputValue)
          }}
        />
      </Box>
 
      <Dialog open={showJobModal} onClose={handleModalClose} fullWidth maxWidth="sm">
        <DialogTitle>{TEXT_SELECT_A_JOB}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder={TEXT_SEARCH_JOBS}
            value={searchInputText}
            onChange={(e) => {
              let inputValue = e.target.value;
              inputValue = inputValue.replace(/^[^A-Za-z0-9]+/, "");
              setSearchInputText(inputValue);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton onClick={handleSearchIconClick}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment:
                searchInputText !== "" ? (
                  <InputAdornment position="end">
                    <IconButton sx={{ color:selectedClientData?.AccentColor !==undefined &&selectedClientData?.AccentColor ? selectedClientData?.AccentColor : BILightGrey }} onClick={handleClearSearch}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null
            }}
            sx={{ mb: 2 }}
          />

          <List>
            {jobLists.length > 0 ? (
              jobLists.map((job) => (
                <ListItem key={job.id} disablePadding>
                  <ListItemButton onClick={() => handleJobSelect(job)}>
                    <ListItemText primary={job.label} />
                  </ListItemButton>
                </ListItem>
              ))
            ) : (
              <Typography textAlign="center" sx={{ color: "text.secondary" }}>
                {TEXT_NO_JOB}
              </Typography>
            )}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GeneralComponent;
